import type { QuestionWithResponse } from '@amzn/gbsa-helios-typescript-client';
import type {
  GetQuestionRequest,
  Question,
  SubQuestion,
} from 'src/types/helios/Question';
import { getLocalizedQuestionContent } from '../../../shared/domain';
import { isDataCollectionType } from '../../../shared/domain/responseTypeClassification';
import {
  backfillErtFields,
  backfillScoredRequirementFlags,
} from '../../../shared/domain/subQuestionCanonicalFields';
import {
  deriveParentCompletion,
  deriveParentResult,
  scoredDefs,
} from './deriveParentResult';
import { resolveVisibleSubQuestions } from './resolveVisibleSubQuestions';

/** Pure render-time helpers for the mobile Perform Audit page. */

/**
 * Whether `alias` may answer this audit. An audit with no assignees
 * is treated as answerable by anyone who holds the perform
 * permission (the permission check is the caller's responsibility);
 * once assignees exist, only those aliases qualify. Case-insensitive.
 */
export function isUserAuditAssignee(
  assignees: readonly string[],
  alias: string,
): boolean {
  if (assignees.length === 0) return true;
  const normalized = alias.toLowerCase();
  return assignees.some((a) => a.toLowerCase() === normalized);
}

/** Inputs to {@link computePerformAuditReadOnly}. */
export interface PerformAuditReadOnlyInput {
  /** URL `mode=view` flag — a completed/expired audit opened read-only. */
  isViewMode: boolean;
  /** True when the audit status still permits editing (canManageAssignees). */
  isEditableStatus: boolean;
  /** Whether the auditor holds Perform*Audit permission for this type. */
  canPerformAudit: boolean;
  /** Whether the auditor is an assignee (see {@link isUserAuditAssignee}). */
  isCurrentUserAssignee: boolean;
}

/**
 * Whether the perform-audit answer surface is read-only. Read-only
 * when ANY of these hold: the URL requests view-mode, the status is
 * past editing, the auditor lacks the perform permission (PA-02), or
 * the auditor is not an assignee (PA-01).
 */
export function computePerformAuditReadOnly({
  isViewMode,
  isEditableStatus,
  canPerformAudit,
  isCurrentUserAssignee,
}: PerformAuditReadOnlyInput): boolean {
  return isViewMode || !isEditableStatus || !canPerformAudit || !isCurrentUserAssignee;
}

/**
 * Per-question fetch input from the summary row. Empty-string
 * fallbacks are safe — callers gate the fetch with `enabled`.
 */
export function buildQuestionQueryInput(
  currentSummary: QuestionWithResponse | undefined,
  businessUnit: string | undefined,
  language: string,
): GetQuestionRequest {
  const details = currentSummary?.questionDetails;
  return {
    programId: details?.program ?? '',
    businessUnit: businessUnit ?? '',
    divisionName: details?.divisionName ?? '',
    questionId: details?.questionId ?? '',
    ...(language ? { language } : {}),
    ...(details?.version ? { version: details.version } : {}),
  };
}

/** Completion metrics resolved for the sticky top nav. */
export interface AuditCompletionMetrics {
  /** Number of questions the auditor has responded to. */
  completed: number;
  /** Total questions denominator — the questions array length. */
  total: number;
  /** Rounded percentage in [0, 100]. Safe for divide-by-zero (0 when total is 0). */
  percent: number;
}

/**
 * Sticky-nav completion, derived client-side (server metrics are
 * eventual and can disagree with the badges). Mixed parents count as
 * completed via deriveParentCompletion — see the tests.
 */
export function computeAuditCompletionMetrics(
  questions: readonly QuestionWithResponse[],
): AuditCompletionMetrics {
  const total = questions.length;
  const completed = questions.filter(isQuestionCompleted).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, total, percent };
}

// Sub defs come from the summary payload (same source the
// close-audit validator reads) — no full fetch needed. Passing the
// defs (not just the count) lets deriveParentCompletion treat
// data-collection children as answered by responseValue.
//
// Exported so the auto-advance-on-complete predicate can compose it
// with the close-audit violation check for a single "this question
// is done and needs nothing more" gate.
export function isQuestionCompleted(q: QuestionWithResponse): boolean {
  const subDefs = q.questionDetails?.localizedContent?.content?.subQuestions;
  if (subDefs && subDefs.length > 0) {
    const parentResult = q.responseDetails?.mainQuestionResponse?.responseResult;
    // For conditional questions, only visible subs count toward completion —
    // hidden subs (wrong parent result) cannot be answered and must not block
    // progress. For non-conditional questions, all subs pass through unchanged.
    // See resolveVisibleSubQuestions — the same helper the answer view and
    // the flyout nav use so every surface computes visibility identically.
    const visibleDefs = resolveVisibleSubQuestions(
      subDefs,
      Boolean(q.questionDetails?.hasConditionalSubQuestions),
      parentResult,
    );
    // If no subs are visible yet (parent unanswered on a conditional question),
    // the question is not complete.
    if (visibleDefs.length === 0) {
      return parentResult != null;
    }
    const childrenComplete = deriveParentCompletion(
      q.responseDetails?.subQuestionResponses,
      visibleDefs,
    );
    // Self-scoring parents (data-only: zero PASS_FAIL subs) require an explicit
    // parent Pass/Fail/NA score in addition to all sub-values being filled — V1
    // parity: V1's progress pie chart keys purely on responseResult. Without this
    // check, the sticky-nav progress bar can reach 100% while the close-audit gate
    // (which also requires responseResult) still blocks. Mixed parents (at least one
    // scored sub) derive their result from the subs, so they never have a committed
    // responseResult before the subs complete — no extra guard needed there.
    const isSelfScoring = visibleDefs.every((def) =>
      isDataCollectionType(def.responseType),
    );
    if (isSelfScoring) {
      return childrenComplete && parentResult != null;
    }
    return childrenComplete;
  }
  return q.responseDetails?.mainQuestionResponse?.responseResult != null;
}

/** Fields the perform-audit question card renders. */
export interface QuestionDisplayFields {
  questionId: string;
  title: string;
  questionText: string;
  instruction: string;
  passCriteria: string;
  failCriteria: string;
  justification: string;
  result: string | undefined;
}

/**
 * Question-card fields. Prefers the full question's localized content
 * (paired with presigned media); falls back to the summary payload so
 * text renders while the per-question fetch is in flight.
 */
export function computeQuestionDisplayFields(
  currentSummary: QuestionWithResponse | undefined,
  fullQuestion: Question | undefined,
  language: string,
): QuestionDisplayFields {
  const currentDetails = currentSummary?.questionDetails;
  const fullDetails = getLocalizedQuestionContent(
    fullQuestion,
    language,
  )?.questionDetails;
  const summaryDetails = currentDetails?.localizedContent?.content?.questionDetails;
  const displayDetails = fullDetails ?? summaryDetails;
  return {
    questionId: currentDetails?.questionId ?? '',
    title: displayDetails?.title ?? '',
    questionText: displayDetails?.questionText ?? '',
    instruction: displayDetails?.auditInstruction ?? '',
    passCriteria: displayDetails?.passCriteria ?? '',
    failCriteria: displayDetails?.failCriteria ?? '',
    justification: displayDetails?.justification ?? '',
    result: gateParentResultForDisplay(currentSummary),
  };
}

/**
 * Gates the server-cached parent `responseResult` on partial
 * sub-answer state.
 *
 * Helios's `UpdateAuditQuestionResponse` re-derives and PERSISTS the
 * parent aggregate whenever the incoming request carries
 * `subResponses` — even when the array only contains the ONE sub the
 * auditor just tapped. The server's rule ("any FAIL sub → parent
 * FAIL") over that partial set is not the client-visible contract:
 * the badge must stay Unanswered until every scored sub has been
 * answered (see {@link deriveParentResult} — the V1 read-gate rule
 * this mirrors). Reading the cached value verbatim would let a
 * single FAIL sub-answer flip the parent badge to FAIL while its
 * siblings are still Unanswered — the exact bug PA-SUBQ-01 /
 * PA-SUBQ-04 catch.
 *
 * The gate:
 *   - Flat parent (no sub definitions): pass through unchanged.
 *   - Self-scoring parent (no SCORED children after visibility
 *     filtering — either every sub is data-collection, or every
 *     conditional scored sub is currently hidden): pass through
 *     unchanged. The auditor scored the parent directly and
 *     `deriveParentResult` returns `undefined` here — using it
 *     would strip a legitimately-committed badge.
 *   - Question WITH visible scored sub definitions: recompute via
 *     {@link deriveParentResult} against the currently-visible
 *     subs; if that collapses to undefined (any scored child still
 *     unanswered), drop the cached aggregate. If it produces the
 *     same aggregate as the server, pass through.
 *
 * Conditional-visibility filtering is applied before deriving —
 * {@link deriveParentResult}'s contract requires callers to pass
 * only currently-visible subs (its docblock notes untriggered
 * subs are treated as unanswered and would collapse the aggregate).
 * The visibility filter is keyed off the cached parent value,
 * matching what {@link isQuestionCompleted} and the render layer
 * do — every surface computes visibility from the same source.
 *
 * NOTE: this gates the DISPLAY-side only — the cache itself is left
 * untouched so a poll or refetch that legitimately observes the
 * completed aggregate (all subs answered) recovers the value on
 * the next read.
 */
function gateParentResultForDisplay(
  currentSummary: QuestionWithResponse | undefined,
): string | undefined {
  const cached = currentSummary?.responseDetails?.mainQuestionResponse?.responseResult;
  if (!cached) return cached;
  const subDefs =
    currentSummary?.questionDetails?.localizedContent?.content?.subQuestions ?? [];
  if (subDefs.length === 0) return cached;
  // Filter to visible subs first — hidden conditional scored subs would
  // otherwise register as unanswered and collapse the aggregate to
  // undefined, stripping a legitimately-complete badge (AutoSDE r1p8
  // finding f-8b844b9d, case 1).
  const visibleDefs = resolveVisibleSubQuestions(
    subDefs,
    Boolean(currentSummary?.questionDetails?.hasConditionalSubQuestions),
    cached,
  );
  // Self-scoring parent — no SCORED children after visibility filter.
  // Either the question is entirely data-collection subs (an auditor
  // filling values plus a direct parent Pass/Fail/NA) OR every scored
  // sub is conditionally hidden. In both cases the auditor scored the
  // parent directly; deriveParentResult would return undefined here
  // and drop the badge (AutoSDE r1p8 finding f-8b844b9d, case 2).
  if (scoredDefs(visibleDefs).length === 0) return cached;
  const subResponses = currentSummary?.responseDetails?.subQuestionResponses;
  const derived = deriveParentResult(subResponses, visibleDefs);
  return derived ?? undefined;
}

/** SubQuestion narrowed to a guaranteed id (React key + mutation target). */
export type DisplayableSubQuestion = SubQuestion & { subQuestionId: string };

/**
 * Localized, id-narrowed sub-questions for the current parent.
 * undefined = full question not loaded; [] = flat question.
 * Requirement flags are backfilled from the canonical defs —
 * translated packs carry display text only (see the tests).
 */
export function computeSubQuestionsForDisplay(
  fullQuestion: Question | undefined,
  language: string,
): DisplayableSubQuestion[] | undefined {
  if (!fullQuestion) return undefined;
  const localized = getLocalizedQuestionContent(fullQuestion, language)?.subQuestions;
  if (!localized) return [];
  const canonicalById = new Map<string, SubQuestion>();
  const canonical =
    fullQuestion.localizedContent?.content?.subQuestions ?? fullQuestion.subQuestions;
  for (const def of canonical ?? []) {
    if (def.subQuestionId) canonicalById.set(def.subQuestionId, def);
  }
  return localized
    .filter(
      (sq): sq is DisplayableSubQuestion =>
        typeof sq.subQuestionId === 'string' && sq.subQuestionId.length > 0,
    )
    .map((sq) => overlayRequirementFlags(sq, canonicalById.get(sq.subQuestionId)));
}

/**
 * Backfill missing requirement + ERT fields; a localized field wins
 * when set. Field list and rationale: subQuestionCanonicalFields.
 */
function overlayRequirementFlags(
  sq: DisplayableSubQuestion,
  canonical: SubQuestion | undefined,
): DisplayableSubQuestion {
  if (!canonical) return sq;
  return {
    ...sq,
    ...backfillScoredRequirementFlags(sq, canonical),
    ...backfillErtFields(sq, canonical),
  };
}
